import useSafeState from "../utils/safe-state";
import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    Radio,
    Row,
    Space,
    Spin,
    Tooltip,
    Upload,
    UploadProps
} from "antd";
import React, {useEffect, useRef, useState} from "react";
import {Trans} from "@lingui/macro";
import {openNotification} from "../utils/openNotification";
import L from 'leaflet';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import {useAxios} from "../utils/hooks";
import {
    FolderOpenOutlined,
    InfoCircleTwoTone,
    LeftOutlined,
    RightOutlined,
    TeamOutlined,
    UploadOutlined
} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import ButtonWithIcon from "../components/buttons/button-with-icon";
import ButtonWithIconSuff from "../components/buttons/button-with-icon-suff";
import moment from "moment";


interface Interface {
    data?: any;
    form?: any;
    onSuccess?: () => void;
    mode?: 'Edit' | 'Add new';
}

const getDetailedErrorMessage = (error: any): string => {
    if (error && error.response && error.response.data) {
        const data = error.response.data;

        if (typeof data.message === 'string') {
            return data.message;
        }

        if (typeof data.error === 'string') {
            return data.error;
        }

        if (typeof data === 'string') {
            return data;
        }
    }

    if (error && error.message) {
        return error.message;
    }

    return 'An unexpected error occurred. Please try again.';
};

const wktToGeoJSON = (wkt: string) => {
    if (!wkt) return null;
    try {
        const cleanWkt = wkt.trim();
        if (cleanWkt.startsWith('MULTIPOLYGON')) {
            const contentMatch = cleanWkt.match(/^MULTIPOLYGON\s*\(\((.*)\)\)$/);
            if (contentMatch) {
                const content = contentMatch[1];
                const coordsStr = cleanWkt.replace(/^MULTIPOLYGON\s*\(\(\(/, '').replace(/\)\)\)$/, '');
                
                const points = coordsStr.split(',').map(p => {
                    const [lng, lat] = p.trim().split(/\s+/).map(Number);
                    return [lng, lat];
                });

                return {
                    type: 'Feature',
                    geometry: {
                        type: 'MultiPolygon',
                        coordinates: [[points]] 
                    },
                    properties: {}
                };
            }
        } else if (cleanWkt.startsWith('POLYGON')) {
             const coordsStr = cleanWkt.substring(cleanWkt.indexOf('((') + 2, cleanWkt.lastIndexOf('))'));
             const points = coordsStr.split(',').map(p => {
                 const [lng, lat] = p.trim().split(/\s+/).map(Number);
                 return [lng, lat];
             });
             return {
                 type: 'Feature',
                 geometry: {
                     type: 'Polygon',
                     coordinates: [points]
                 },
                 properties: {}
             };
        }
    } catch (e) {
        console.error("Error parsing WKT", e);
        return null;
    }
    return null;
}

const parseGeometry = (data: any) => {
    if (!data) return null;
    if (typeof data === 'object') return data;
    if (typeof data === 'string') {
        const trimmed = data.trim();
        if (trimmed.startsWith('{')) {
            try {
                return JSON.parse(trimmed);
            } catch (e) {
                console.error("Error parsing GeoJSON string", e);
                return null;
            }
        }
        return wktToGeoJSON(trimmed);
    }
    return null;
};

const AddLocationForm = (props: Interface) => {

    const data = props.data || {};

    const [form] = Form.useForm();
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    
    // Map refs
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
    const [currentGeoJSON, setCurrentGeoJSON] = useSafeState<any>(null);
    const [loading, setLoading] = useSafeState(false);

    const [nationalDatabaseFileList, setNationalDatabaseFileList] = useSafeState<Array<any>>([]);
    const [nationalDatabaseLoading, setNationalDatabaseLoading] = useSafeState(false);

    const [certOrImpactAssessmentFileList, setCertOrImpactAssessmentFileList] = useSafeState<Array<any>>([]);
    const [certOrImpactAssessmentLoading, setCertOrImpactAssessmentLoading] = useSafeState(false);

    const [certEgFSCFileList, setCertEgFSCFileList] = useSafeState<Array<any>>([]);
    const [certEgFSCLoading, setCertEgFSCLoading] = useSafeState(false);

    const [reviewReportsFromThirdPartyVerifiers1FileList, setReviewReportsFromThirdPartyVerifiers1FileList] = useSafeState<Array<any>>([]);
    const [reviewReportsFromThirdPartyVerifiers1Loading, setReviewReportsFromThirdPartyVerifiers1Loading] = useSafeState(false);

    const [dataFromNationalOrLocalLandRegistryFileList, setDataFromNationalOrLocalLandRegistryFileList] = useSafeState<Array<any>>([]);
    const [dataFromNationalOrLocalLandRegistryLoading, setDataFromNationalOrLocalLandRegistryLoading] = useSafeState(false);

    const [publicStatementsFileList, setPublicStatementsFileList] = useSafeState<Array<any>>([]);
    const [publicStatementsLoading, setPublicStatementsLoading] = useSafeState(false);


    const [documentedClaimValue, setDocumentedClaimValue] = useState<number | undefined>(undefined);

    const [indigenousPresence, setIndigenousPresence] = useSafeState<boolean>();
    const [consultedWithNGO, setConsultedWithNGO] = useSafeState<boolean>(true);

    const [currentState, setCurrentState] = useSafeState(0);

    const [drawedGeoJSON, setDrawedGeoJSON] = useState<any>(null);
    const [locationId, setLocationId] = useSafeState<number | null>(null);
    const [mapReady, setMapReady] = useState(false);
    const [initialGeoJSON, setInitialGeoJSON] = useState<any>(null);

    const [anyPublicStatement, setAnyPublicStatement] = useSafeState(false);

    useEffect(() => {

        if (currentState === 0) {
            initializeMap();
        }
        return () => {
            setMapReady(false);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                drawnItemsRef.current = null;
            }
        };
    }, [currentState]);

    useEffect(() => {
        if (mapReady && initialGeoJSON && mapInstanceRef.current && drawnItemsRef.current) {
             loadExistingGeometry(mapInstanceRef.current, drawnItemsRef.current, initialGeoJSON);
             setInitialGeoJSON(null);
        }
    }, [mapReady, initialGeoJSON]);

    useEffect(() => {
        if (props.mode === 'Edit' && props.data && props.data.id) {
            
            form.setFieldsValue({ name: props.data.name });
            
            if (props.data.location) {
                setDrawedGeoJSON(props.data.location);
                const geo = parseGeometry(props.data.location);
                if (geo) {
                    setCurrentGeoJSON(geo);
                    setInitialGeoJSON(geo);
                }
            }

            if (axiosInstance.current) {
                axiosInstance.current.get('/resource/location-data/get-data', { params: { locationId: props.data.id } })
                    .then((response) => {
                        const d = response.data;
                        
                        const p = (val: any) => val ? parseInt(val, 10) : undefined;
                        
                        form.setFieldsValue({
                            indigenous: {
                                presence: p(d.presence),
                                nationalDatabaseDescription: d.nationalDatabaseDescription,
                                consultedWithNGO: p(d.consultedWithNGO),
                                consultedWithNGODescription: d.consultedWithNGODescription,
                                consultedWithLocalIndigenousConcils: p(d.consultedWithLocalIndigenousConcils),
                                isRatifiedTheILO169Convention: p(d.isRatifiedTheILO169Convention),
                                hasFPIC: p(d.hasFPIC),
                                consultationWithIndigenousCommunities: p(d.consultationWithIndigenousCommunities),
                                haveTerritorialClaims: p(d.haveTerritorialClaims),
                                hasAnyLegalAndDocumentedClaimToTheLand: p(d.hasAnyLegalAndDocumentedClaimToTheLand),
                                hasAnyLegalAndDocumentedClaimToTheLandDescription: d.hasAnyLegalAndDocumentedClaimToTheLandDescription,
                                dataFromNationalOrLocalLandRegistryDescription: d.dataFromNationalOrLocalLandRegistryDescription,
                                dataFromNationalOrLocalLandRegistryDeadline: d.dataFromNationalOrLocalLandRegistryDeadline ? moment(d.dataFromNationalOrLocalLandRegistryDeadline) : undefined,
                                isThereCourtLitigationLandDisputeOrOngoingNegotiation: p(d.isThereCourtLitigationLandDisputeOrOngoingNegotiation),
                                anyPublicStatementsOrRecordsOfNonConsensualLandUse: p(d.anyPublicStatementsOrRecordsOfNonConsensualLandUse),
                                publicStatementsDescription: d.publicStatementsDescription,
                            },
                            anyConcerns: {
                                humanRightViolationDatabaseDescription: d.humanRightViolationDatabaseDescription,
                                checkedAmnestyInternational: p(d.checkedAmnestyInternational),
                                checkedHumanRightsWatch: p(d.checkedHumanRightsWatch),
                                checkedOhchr: p(d.checkedOhchr),
                                checkedUsDepartmentOfStateCountryReportsonHumanRights: p(d.checkedUsDepartmentOfStateCountryReportsonHumanRights),
                                isThereLiveSanction: p(d.isThereLiveSanction),
                                scopeOfTheSanction: d.scopeOfTheSanction
                            }
                        });

                        if (d.presence) setIndigenousPresence(p(d.presence) === 1);
                        if (d.consultedWithNGO) setConsultedWithNGO(p(d.consultedWithNGO) === 1);
                        if (d.hasAnyLegalAndDocumentedClaimToTheLand) setDocumentedClaimValue(p(d.hasAnyLegalAndDocumentedClaimToTheLand));
                        if (d.anyPublicStatementsOrRecordsOfNonConsensualLandUse) setAnyPublicStatement(p(d.anyPublicStatementsOrRecordsOfNonConsensualLandUse) === 1)

                        const mapFile = (f: any) => f ? [{ uid: f.id, name: f.fileName, status: 'done', url: `${process.env.REACT_APP_API_BASE_URL}/public/file/get?folder=${f.bucketName}&filename=${f.filePath}` }] : [];
                        
                        setNationalDatabaseFileList(mapFile(d.nationalDatabase));
                        setCertOrImpactAssessmentFileList(mapFile(d.certOrImpactAssessment));
                        setCertEgFSCFileList(mapFile(d.certEgFSC));
                        setReviewReportsFromThirdPartyVerifiers1FileList(mapFile(d.reviewReportsFromThirdPartyVerifiers1));
                        setDataFromNationalOrLocalLandRegistryFileList(mapFile(d.dataFromNationalOrLocalLandRegistry));
                        setPublicStatementsFileList(mapFile(d.publicStatements));
                    })
                    .catch(err => {
                        console.error("Error loading data", err);
                        openNotification('error', 'Failed to load location data');
                    })
                    .finally(() => setLoading(false));
            }
        } else if (data && data.location) {
            setDrawedGeoJSON(data.location);
            const geo = parseGeometry(data.location);
            if (geo) {
                setCurrentGeoJSON(geo);
                setInitialGeoJSON(geo);
            }
        }
    }, [props.mode, props.data]);

    useEffect(() => {

        setDrawedGeoJSON(JSON.stringify(currentGeoJSON));

        setTimeout(() => {
            loadExistingGeometry(mapInstanceRef.current, drawnItemsRef.current);
        }, 150);
    }, [currentState]);

    const loadExistingGeometry = (map: any, drawnItems: any, geometryData?: any) => {

        if (!map || !drawnItems) return;
        
        const dataToLoad = geometryData || drawedGeoJSON;
        if (!dataToLoad) return;


        try {
            if (dataToLoad) {
                const geoJSON = parseGeometry(dataToLoad);

                if (!geoJSON) return;

                drawnItems.clearLayers();

                const geoJsonLayer = L.geoJSON(geoJSON, {
                    style: {
                        color: '#3388ff',
                        weight: 3,
                        fillOpacity: 0.2
                    }
                });

                geoJsonLayer.eachLayer((layer: any) => {
                    drawnItems.addLayer(layer);
                });

                if (!currentGeoJSON) {
                    setCurrentGeoJSON(geoJSON);
                }

                // Fit map to geometry

                const bounds = geoJsonLayer.getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [20, 20] });
                }
            }
        } catch (error) {
            console.error('Error loading existing geometry:', error);
            openNotification('error', 'Could not load existing geometry for editing');
        }
    };

    const saveLocationData = (locationId: any) => {

        if(locationId == null){
            return;
        }

        form.validateFields([
            ["indigenous", "presence"],
            ["indigenous", "nationalDatabaseDescription"],
            ["indigenous", "consultedWithNGO"],
            ["indigenous", "consultedWithNGODescription"],
            ["indigenous", "consultedWithLocalIndigenousConcils"],
            ["indigenous", "isRatifiedTheILO169Convention"],
            ["indigenous", "hasFPIC"],
            ["indigenous", "consultationWithIndigenousCommunities"],
            ["indigenous", "haveTerritorialClaims"],
            ["indigenous", "hasAnyLegalAndDocumentedClaimToTheLand"],
            ["indigenous", "hasAnyLegalAndDocumentedClaimToTheLandDescription"],
            ["indigenous", "dataFromNationalOrLocalLandRegistryDescription"],
            ["indigenous", "dataFromNationalOrLocalLandRegistryDeadline"],
            ["indigenous", "isThereCourtLitigationLandDisputeOrOngoingNegotiation"],
            ["indigenous", "anyPublicStatementsOrRecordsOfNonConsensualLandUse"],
            ["indigenous", "publicStatementsDescription"],
            ["anyConcerns", "humanRightViolationDatabaseDescription"],
            ["anyConcerns", "checkedAmnestyInternational"],
            ["anyConcerns", "checkedHumanRightsWatch"],
            ["anyConcerns", "checkedOhchr"],
            ["anyConcerns", "checkedUsDepartmentOfStateCountryReportsonHumanRights"],
            ["anyConcerns", "isThereLiveSanction"],
            ["anyConcerns", "scopeOfTheSanction"]
        ]).then(() => {

            setLoading(true);
            
            if (axiosInstance.current != null) {
                axiosInstance.current
                    .post('/resource/location-data/save-data?locationId=' + locationId, {

                            presence: form.getFieldValue(["indigenous", "presence"]),
                            nationalDatabaseDescription: form.getFieldValue(["indigenous", "nationalDatabaseDescription"]),
                            consultedWithNGO: form.getFieldValue(["indigenous", "consultedWithNGO"]),
                            consultedWithNGODescription: form.getFieldValue(["indigenous", "consultedWithNGODescription"]),
                            consultedWithLocalIndigenousConcils: form.getFieldValue(["indigenous", "consultedWithLocalIndigenousConcils"]),
                            isRatifiedTheILO169Convention: form.getFieldValue(["indigenous", "isRatifiedTheILO169Convention"]),
                            hasFPIC: form.getFieldValue(["indigenous", "hasFPIC"]),
                            consultationWithIndigenousCommunities: form.getFieldValue(["indigenous", "consultationWithIndigenousCommunities"]),
                            haveTerritorialClaims: form.getFieldValue(["indigenous", "haveTerritorialClaims"]),
                            hasAnyLegalAndDocumentedClaimToTheLand: form.getFieldValue(["indigenous", "hasAnyLegalAndDocumentedClaimToTheLand"]),
                            hasAnyLegalAndDocumentedClaimToTheLandDescription: form.getFieldValue(["indigenous", "hasAnyLegalAndDocumentedClaimToTheLandDescription"]),
                            dataFromNationalOrLocalLandRegistryDescription: form.getFieldValue(["indigenous", "dataFromNationalOrLocalLandRegistryDescription"]),
                            dataFromNationalOrLocalLandRegistryDeadline: form.getFieldValue(["indigenous", "dataFromNationalOrLocalLandRegistryDeadline"]),
                            isThereCourtLitigationLandDisputeOrOngoingNegotiation: form.getFieldValue(["indigenous", "isThereCourtLitigationLandDisputeOrOngoingNegotiation"]),
                            anyPublicStatementsOrRecordsOfNonConsensualLandUse: form.getFieldValue(["indigenous", "anyPublicStatementsOrRecordsOfNonConsensualLandUse"]),
                            publicStatementsDescription: form.getFieldValue(["indigenous", "publicStatementsDescription"]),
                            humanRightViolationDatabaseDescription: form.getFieldValue(["anyConcerns", "humanRightViolationDatabaseDescription"]),
                            checkedAmnestyInternational: form.getFieldValue(["anyConcerns", "checkedAmnestyInternational"]),
                            checkedHumanRightsWatch: form.getFieldValue(["anyConcerns", "checkedHumanRightsWatch"]),
                            checkedOhchr: form.getFieldValue(["anyConcerns", "checkedOhchr"]),
                            checkedUsDepartmentOfStateCountryReportsonHumanRights: form.getFieldValue(["anyConcerns", "checkedUsDepartmentOfStateCountryReportsonHumanRights"]),
                            isThereLiveSanction: form.getFieldValue(["anyConcerns", "isThereLiveSanction"]),
                            scopeOfTheSanction: form.getFieldValue(["anyConcerns", "scopeOfTheSanction"])
                        }, {}
                    )
                    .then(() => {
                        openNotification('success', 'Location saved successfully');
                        if (props.onSuccess) props.onSuccess();
                    })
                    .catch(function (error: any) {
                        openNotification('error', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    }


    const saveLocation = () => {

        if (!currentGeoJSON) {
            openNotification('error', 'Please draw a location on the map');
            return;
        }

        if (!form.getFieldValue(['name']) || form.getFieldValue(['name']).trim() === '') {
            openNotification('error', 'Please enter a location name');
            return;
        }

        const geometry = currentGeoJSON.geometry || currentGeoJSON;
        const coordinates = geometry.coordinates;
        let wkt = '';

        if (geometry.type === 'Polygon') {
            const polygonCoords = coordinates[0].map((coord: number[]) =>
                `${coord[0]} ${coord[1]}`
            ).join(', ');
            wkt = `MULTIPOLYGON(((${polygonCoords})))`;
        } else if (geometry.type === 'MultiPolygon') {
            const polygons = coordinates.map((polygon: any) => {
                const coords = polygon[0].map((coord: number[]) =>
                    `${coord[0]} ${coord[1]}`
                ).join(', ');
                return `((${coords}))`;
            }).join(', ');
            wkt = `MULTIPOLYGON(${polygons})`;
        }

        const payload = {
            name: form.getFieldValue(['name']).trim(),
            location: wkt,
            source: 'RAW_MATERIAL'
        };


        if (axiosInstance.current) {
            const request = props.mode === 'Edit' && props.data && props.data.id
                ? axiosInstance.current.put('/resource/locations/update-location/' + props.data.id, payload)
                : axiosInstance.current.post('/resource/locations/add-location', payload);

            request
                .then((response) => {
                    setLocationId(response.data.id);
                    setCurrentState(currentState + 1);
                    handleScrollToTop();
                    const id = props.mode === 'Edit' ? props.data.id : response.data.id;
                })
                .catch((error) => {
                    console.error('Error saving location:', error);
                    let errorMessage = 'Failed to save location';
                    if (error && error.response && error.response.data) {
                        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
                    } else if (error && error.message) {
                        errorMessage = error.message;
                    }
                    openNotification('error', errorMessage);
                    setLoading(false);
                });
        }

    };

    const handleNext = () => {
        saveLocation();
    };
    const handlePrev = () => {
        setCurrentState(currentState - 1);
        handleScrollToTop();
    };

    const handleScrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "auto", // instant scroll
        });
    };

    const initializeMap = () => {

        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
            drawnItemsRef.current = null;
        }

        if (!mapRef.current || mapInstanceRef.current) return;

        try {
            const map = L.map(mapRef.current, {
                center: [47.5, 19.0], // Hungary center
                zoom: 7,
                zoomControl: true,
            });

            mapInstanceRef.current = map;

            // Add OpenStreetMap tiles
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
                maxZoom: 18,
            }).addTo(map);

            // Create feature group for drawn items
            const drawnItems = new L.FeatureGroup();
            map.addLayer(drawnItems);
            drawnItemsRef.current = drawnItems;

            // Add draw control - only allow polygon drawing
            const drawControl = new (L.Control as any).Draw({
                position: 'topright',
                draw: {
                    polygon: {
                        allowIntersection: false,
                        drawError: {
                            color: '#e1e100',
                            message: '<strong>Error:</strong> Shape cannot intersect itself!'
                        },
                        shapeOptions: {
                            color: '#1890ff',
                            fillColor: '#1890ff',
                            fillOpacity: 0.3,
                            weight: 2
                        }
                    },
                    rectangle: {
                        shapeOptions: {
                            color: '#1890ff',
                            fillColor: '#1890ff',
                            fillOpacity: 0.3,
                            weight: 2
                        }
                    },
                    circle: false,
                    circlemarker: false,
                    marker: false,
                    polyline: false,
                },
                edit: {
                    featureGroup: drawnItems,
                    remove: true
                }
            });

            map.addControl(drawControl);

            // Handle draw events
            map.on('draw:created', (e: any) => {
                // Clear previous drawings
                drawnItems.clearLayers();
                
                // Add new drawing
                drawnItems.addLayer(e.layer);
                
                const geoJSON = e.layer.toGeoJSON();
                console.log('GeoJSON created:', geoJSON);
                setCurrentGeoJSON(geoJSON);
            });

            map.on('draw:edited', (e: any) => {
                const layers = e.layers;
                layers.eachLayer((layer: any) => {
                    const geoJSON = layer.toGeoJSON();
                    setCurrentGeoJSON(geoJSON);
                });
            });

            map.on('draw:deleted', () => {
                setCurrentGeoJSON(null);
            });

            // Force resize
            setTimeout(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.invalidateSize();
                    setMapReady(true);
                }
            }, 100);

        } catch (error) {
            console.error('Error initializing map:', error);
            openNotification('error', 'Failed to initialize map');
        }
    };

    const handleFinish = (values: any) => {
    };

    const onReset = () => {
        form.resetFields();
        setCurrentGeoJSON(null);
        if (drawnItemsRef.current) {
            drawnItemsRef.current.clearLayers();
        }
    };

    const natDatabaseProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/location-assignment-rules/upload-csv',
        multiple: false,
        fileList: nationalDatabaseFileList,
        onPreview: async (file: any) => {
            if (file.url && axiosInstance.current) {
                try {
                    const response = await axiosInstance.current.get(file.url, { responseType: 'blob' });
                    const FileDownload = require('js-file-download');
                    FileDownload(response.data, file.name);
                } catch (error) {
                    console.error("Download failed", error);
                    window.open(file.url, '_blank');
                }
            }
        },
        onRemove: (file) => {
            handleRemoveFile('indigenous.nationalDatabase');
        },
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                openNotification('success', `${info.file.name} file uploaded successfully`);
            } else if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = nationalDatabaseFileList;
            fileList2.push(file);
            setNationalDatabaseFileList(fileList2);
            return false;
        },
    };

    const certOrImpactAssessmentProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/location-assignment-rules/upload-csv',
        multiple: false,
        fileList: certOrImpactAssessmentFileList,
        onPreview: async (file: any) => {
            if (file.url && axiosInstance.current) {
                try {
                    const response = await axiosInstance.current.get(file.url, { responseType: 'blob' });
                    const FileDownload = require('js-file-download');
                    FileDownload(response.data, file.name);
                } catch (error) {
                    console.error("Download failed", error);
                    window.open(file.url, '_blank');
                }
            }
        },
        onRemove: (file) => {
            handleRemoveFile('indigenous.certOrImpactAssessment');
        },
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                openNotification('success', `${info.file.name} file uploaded successfully`);
            } else if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = certOrImpactAssessmentFileList;
            fileList2.push(file);
            setCertOrImpactAssessmentFileList(fileList2);
            return false;
        },
    };

    const certEgFSCProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/location-assignment-rules/upload-csv',
        multiple: false,
        fileList: certEgFSCFileList,
        onPreview: async (file: any) => {
            if (file.url && axiosInstance.current) {
                try {
                    const response = await axiosInstance.current.get(file.url, { responseType: 'blob' });
                    const FileDownload = require('js-file-download');
                    FileDownload(response.data, file.name);
                } catch (error) {
                    console.error("Download failed", error);
                    window.open(file.url, '_blank');
                }
            }
        },
        onRemove: (file) => {
            handleRemoveFile('indigenous.certEgFSC');
        },
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                openNotification('success', `${info.file.name} file uploaded successfully`);
            } else if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = certEgFSCFileList;
            fileList2.push(file);
            setCertEgFSCFileList(fileList2);
            return false;
        },
    };

    const reviewReportsFromThirdPartyVerifiers1Props: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/location-assignment-rules/upload-csv',
        multiple: false,
        fileList: reviewReportsFromThirdPartyVerifiers1FileList,
        onPreview: async (file: any) => {
            if (file.url && axiosInstance.current) {
                try {
                    const response = await axiosInstance.current.get(file.url, { responseType: 'blob' });
                    const FileDownload = require('js-file-download');
                    FileDownload(response.data, file.name);
                } catch (error) {
                    console.error("Download failed", error);
                    window.open(file.url, '_blank');
                }
            }
        },
        onRemove: (file) => {
            handleRemoveFile('indigenous.reviewReportsFromThirdPartyVerifiers1');
        },
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                openNotification('success', `${info.file.name} file uploaded successfully`);
            } else if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = reviewReportsFromThirdPartyVerifiers1FileList;
            fileList2.push(file);
            setReviewReportsFromThirdPartyVerifiers1FileList(fileList2);
            return false;
        },
    };

    const dataFromNationalOrLocalLandRegistryProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/location-assignment-rules/upload-csv',
        multiple: false,
        fileList: dataFromNationalOrLocalLandRegistryFileList,
        onPreview: async (file: any) => {
            if (file.url && axiosInstance.current) {
                try {
                    const response = await axiosInstance.current.get(file.url, { responseType: 'blob' });
                    const FileDownload = require('js-file-download');
                    FileDownload(response.data, file.name);
                } catch (error) {
                    console.error("Download failed", error);
                    window.open(file.url, '_blank');
                }
            }
        },
        onRemove: (file) => {
            handleRemoveFile('indigenous.dataFromNationalOrLocalLandRegistry');
        },
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                openNotification('success', `${info.file.name} file uploaded successfully`);
            } else if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = dataFromNationalOrLocalLandRegistryFileList;
            fileList2.push(file);
            setDataFromNationalOrLocalLandRegistryFileList(fileList2);
            return false;
        },
    };

    const publicStatementsProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/location-assignment-rules/upload-csv',
        multiple: false,
        fileList: publicStatementsFileList,
        onPreview: async (file: any) => {
            if (file.url && axiosInstance.current) {
                try {
                    const response = await axiosInstance.current.get(file.url, { responseType: 'blob' });
                    const FileDownload = require('js-file-download');
                    FileDownload(response.data, file.name);
                } catch (error) {
                    console.error("Download failed", error);
                    window.open(file.url, '_blank');
                }
            }
        },
        onRemove: (file) => {
            handleRemoveFile('indigenous.publicStatements');
        },
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                openNotification('success', `${info.file.name} file uploaded successfully`);
            } else if (info.file.status === 'error') {
                openNotification('error', `${info.file.name} file upload failed.`);
            }
        },
        beforeUpload(file: any) {
            let fileList2: Array<any> = publicStatementsFileList;
            fileList2.push(file);
            setPublicStatementsFileList(fileList2);
            return false;
        },
    };
    const handleLoading = (onboardingDataType: any, value: boolean) => {
        switch(onboardingDataType){
            case 'indigenous.nationalDatabase':
                setNationalDatabaseLoading(value)
                break;
            case 'indigenous.certOrImpactAssessment':
                setCertOrImpactAssessmentLoading(value);
                break;
            case 'indigenous.certEgFSC':
                setCertEgFSCLoading(value);
                break;
            case 'indigenous.reviewReportsFromThirdPartyVerifiers1':
                setReviewReportsFromThirdPartyVerifiers1Loading(value);
                break;
            case 'indigenous.dataFromNationalOrLocalLandRegistry':
                setDataFromNationalOrLocalLandRegistryLoading(value);
                break;
            case 'indigenous.publicStatements':
                setPublicStatementsLoading(value);
                break;
        }
    }

    const handleFileList = (onboardingDataType: any) => {
        switch(onboardingDataType){
            case 'indigenous.nationalDatabase':
                setNationalDatabaseFileList([]);
                break;
            case 'indigenous.certOrImpactAssessment':
                setCertOrImpactAssessmentFileList([]);
                break;
            case 'indigenous.certEgFSC':
                setCertEgFSCFileList([]);
                break;
            case 'indigenous.reviewReportsFromThirdPartyVerifiers1':
                setReviewReportsFromThirdPartyVerifiers1FileList([]);
                break;
            case 'indigenous.dataFromNationalOrLocalLandRegistry':
                setDataFromNationalOrLocalLandRegistryFileList([]);
                break;
            case 'indigenous.publicStatements':
                setPublicStatementsFileList([]);
                break;
        }
    }
    const getFormData = (onboardingDataType: any): FormData => {

        const formData = new FormData();

        switch(onboardingDataType){
            case 'indigenous.nationalDatabase':
                nationalDatabaseFileList.forEach(file => { formData.append('file', file); });
                break;
            case 'indigenous.certOrImpactAssessment':
                certOrImpactAssessmentFileList.forEach(file => { formData.append('file', file); });
                break;
            case 'indigenous.certEgFSC':
                certEgFSCFileList.forEach(file => { formData.append('file', file); });
                break;
            case 'indigenous.reviewReportsFromThirdPartyVerifiers1':
                reviewReportsFromThirdPartyVerifiers1FileList.forEach(file => { formData.append('file', file); });
                break;
            case 'indigenous.dataFromNationalOrLocalLandRegistry':
                dataFromNationalOrLocalLandRegistryFileList.forEach(file => { formData.append('file', file); });
                break;
            case 'indigenous.publicStatements':
                publicStatementsFileList.forEach(file => { formData.append('file', file); });
                break;
        }

        formData.append('locationDataType', onboardingDataType)

        return formData;
    }
    const getFileListForType = (onboardingDataType: any): Array<any> => {
        switch(onboardingDataType){
            case 'indigenous.nationalDatabase':
                return nationalDatabaseFileList;
            case 'indigenous.certOrImpactAssessment':
                return certOrImpactAssessmentFileList;
            case 'indigenous.certEgFSC':
                return certEgFSCFileList;
            case 'indigenous.reviewReportsFromThirdPartyVerifiers1':
                return reviewReportsFromThirdPartyVerifiers1FileList;
            case 'indigenous.dataFromNationalOrLocalLandRegistry':
                return dataFromNationalOrLocalLandRegistryFileList;
            case 'indigenous.publicStatements':
                return publicStatementsFileList;
            default:
                return [];
        }
    }

    const handleRemoveFile = (locationDataType: any) => {
        if (axiosInstance.current != null) {
            handleLoading(locationDataType, true);

            axiosInstance.current
                .delete('/resource/location-data/delete-doc?locationId=' + locationId + '&locationDataType=' + locationDataType, {})
                .then(() => {
                    openNotification('success', 'File successfully deleted');
                    handleFileList(locationDataType);
                })
                .catch(function (error) {
                    const errorMessage = getDetailedErrorMessage(error);
                    openNotification('error', errorMessage);
                })
                .finally(() => {
                    handleLoading(locationDataType, false);
                });
        }
    };

    const handleUploadFile = (onboardingDataType: any, locationId: any) => {

        if(locationId == null){
            return;
        }

        const fileList = getFileListForType(onboardingDataType);
        if (!fileList || fileList.length === 0) {
            openNotification(
                'error',
                'No file selected. Please select a file to upload.'
            );
            return;
        }

        if (axiosInstance.current != null) {
            handleLoading(onboardingDataType, true);

            axiosInstance.current
                .post('/resource/location-data/upload-doc?locationId=' + locationId, getFormData(onboardingDataType), {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(() => {
                    openNotification('customSuccess', 'File successfully uploaded');
                })
                .catch(function (error) {
                    const errorMessage = getDetailedErrorMessage(error);
                    openNotification('error', errorMessage);
                })
                .finally(() => {
                    handleFileList(onboardingDataType);
                    handleLoading(onboardingDataType, false);
                });
        }
    };

    function renderContent(state: any) {
        switch (state) {
            case 0:
                return (
                    <Card
                        title={<><Trans>Step 1: Draw a geolocation</Trans></>}
                        bordered={false}
                        style={{width: 980, marginTop: -25, marginLeft: -20}}
                    >
                        <Form
                            id="addLocation"
                            layout='vertical'
                            form={form}
                        >
                            <Spin spinning={loading}>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item
                                            label={<Trans>Location Name</Trans>}
                                            name="name"
                                            rules={[{ required: true, message: 'Please enter a location name' }]}
                                        >
                                            <Input placeholder="Enter location name" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item label={<Trans>Draw Location Area</Trans>}>
                                            <div
                                                ref={mapRef}
                                                style={{
                                                    height: '641px',
                                                    width: '100%',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f5f5f5'
                                                }}
                                            />
                                            <div style={{ marginTop: '8px', color: '#8c8c8c', fontSize: '12px' }}>
                                                <Trans>Use the drawing tools on the right to draw a polygon or rectangle on the map</Trans>
                                            </div>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Spin>
                        </Form>
                    </Card>
                );
            case 1:
                return (
                <Form form={form} component={false}>
                    <Card
                        title={<><Trans>Step 2: Indigenous</Trans></>}
                        bordered={false}
                        style={{width: 980, marginTop: -25, marginLeft: -20}}
                    >
                        <b>
                            <Trans>
                                1. Is there an indigenous presence in the country of production or parts of it?
                            </Trans>
                        </b>
                        <Form.Item
                            name={[ "indigenous", "presence"]}

                            rules={[{required: true, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setIndigenousPresence(value === 1);
                                }}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Divider></Divider>
                        <b>
                            <Trans>
                                2. Please attach the national database on indigenous presence, if available. And
                                write
                                a
                                few sentences about what it contains.
                            </Trans>
                        </b>
                        <Form.Item
                            style={{marginBottom: 0}}
                            name={[ "indigenous", "nationalDatabase"]}
                        >
                            <div style={{marginBottom: 10}}>
                                <Upload {...natDatabaseProps}>
                                    <Button
                                        disabled={!indigenousPresence}
                                        style={{marginTop: 10}}
                                        icon={<FolderOpenOutlined/>}
                                    > <Trans>Select File</Trans></Button>
                                </Upload>
                            </div>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={[ "indigenous", "nationalDatabaseDescription"]}
                        >
                            <TextArea disabled={!indigenousPresence} rows={4} placeholder="What contains the provided document?"/>
                        </Form.Item>
                        <div className="center-card">
                            <Button
                                disabled={!indigenousPresence}
                                loading={nationalDatabaseLoading} htmlType="button" type="primary"
                                onClick={() => handleUploadFile('indigenous.nationalDatabase', locationId)}>
                                <ButtonWithIcon
                                    icon={<UploadOutlined/>}
                                    text={<Trans>Upload</Trans>}
                                />
                            </Button>
                        </div>

                        <Divider></Divider>
                        <Form.Item style={{marginTop: 10}}>
                            <b><Trans>3. Provide the Certifications or Imapct assessment (if any).</Trans></b>
                            <Tooltip
                                placement="top"
                                title={
                                    <>
                                        <div>
                                            <Trans>
                                                e.g.: Forest Stewardship Council (FSC) and Roundtable on Sustainable Palm Oil (RSPO)
                                            </Trans>
                                        </div>
                                        <div>
                                            <Trans>
                                                ESIA: Environmental and social impact assessments. (Part of the investment approval process)
                                                Countries often have ESIA laws that include indigenous consultation as part of the project approval process.
                                                Review these assessments for evidence of proper consultation.
                                            </Trans>
                                        </div>
                                    </>
                                }
                                children={
                                    <InfoCircleTwoTone
                                        twoToneColor="#1890ff"
                                        style={{ marginLeft: 5 }}
                                    />
                                }
                            />
                            <div style={{marginBottom: 10}}>
                                <Upload {...certOrImpactAssessmentProps}>
                                    <Button
                                        disabled={!indigenousPresence}
                                        style={{marginTop: 10}}
                                        icon={<FolderOpenOutlined/>}
                                    > <Trans>Select File</Trans></Button>
                                </Upload>
                            </div>
                            <div className="center-card">
                                <Button
                                    disabled={!indigenousPresence}
                                    loading={certOrImpactAssessmentLoading} htmlType="button" type="primary"
                                    onClick={() => handleUploadFile('indigenous.certOrImpactAssessment', locationId)}>
                                    <ButtonWithIcon
                                        icon={<UploadOutlined/>}
                                        text={<Trans>Upload</Trans>}
                                    />
                                </Button>
                            </div>
                        </Form.Item>
                        <Divider></Divider>
                        <b><Trans>4. Have you consulted with NGOs?</Trans></b>
                        <Form.Item
                            name={[ "indigenous", "consultedWithNGO"]}
                            rules={[{required: indigenousPresence, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                disabled={!indigenousPresence}
                                onChange={(e) => {
                                    setConsultedWithNGO(e.target.value === 1);
                                }}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Consultion done</Radio>
                                    <Radio value={2}>Consultation is not necessary</Radio>
                                    <Radio value={3}>Consultation in progress</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={[ "indigenous", "consultedWithNGODescription"]}
                        >
                            <TextArea disabled={!consultedWithNGO || !indigenousPresence} rows={4} placeholder="Please specify details in a few sentences.."/>
                        </Form.Item>
                        <Divider></Divider>
                        <b><Trans>5. Have you consulted with local indigenous Concils?</Trans></b>
                        <Tooltip
                            placement="top"
                            title={
                                <>
                                    <div>
                                        <Trans>
                                            Written engagement, documents shall be at hand upon authority request. Supportive documents.
                                        </Trans>
                                    </div>
                                </>
                            }
                            children={
                                <InfoCircleTwoTone
                                    twoToneColor="#1890ff"
                                    style={{ marginLeft: 5 }}
                                />
                            }
                        />
                        <Form.Item
                            name={[ "indigenous", "consultedWithLocalIndigenousConcils"]}
                            rules={[{required: indigenousPresence, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                disabled={!indigenousPresence}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Divider></Divider>
                        <b>
                            <Trans>6. Country of production is ratified the ILO 169 Convention or not (Indigenous and Tribal
                                Peoples Convention = key legal standard for consultation rights)?
                            </Trans>
                        </b>
                        <i style={{marginLeft: 5}}>Please check here: </i><a href="https://normlex.ilo.org/dyn/nrmlx_en/f?p=1000:11300:0::NO:11300:P11300_INSTRUMENT_ID:312314" target="_blank"> www.normlex.ilo.org</a>
                        <Form.Item
                            name={[ "indigenous", "isRatifiedTheILO169Convention"]}
                            rules={[{required: indigenousPresence, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                disabled={!indigenousPresence}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Divider></Divider>
                        <b><Trans>7. Has Free, Prior and Informed Consent (FPIC) been obtained where applicable?</Trans></b>
                        <i style={{marginLeft: 5}}>You can read about FPIC
                            <a
                                href="https://www.ohchr.org/en/indigenous-peoples/consultation-and-free-prior-and-informed-consent-fpic"
                                target="_blank"> here</a>
                        </i>
                        <Form.Item
                            name={[ "indigenous", "hasFPIC"]}
                            rules={[{required: indigenousPresence, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                disabled={!indigenousPresence}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Not applicable</Radio>
                                    <Radio value={2}>Yes obtained</Radio>
                                    <Radio value={3}>Not obtained</Radio>
                                    <Radio value={4}>Unknown to obtained</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Divider></Divider>
                        <Form.Item style={{marginTop: 10}}>
                            <b><Trans>8. Please attach the certification, if applicable! (eg.:FSC)</Trans></b>
                            <Tooltip
                                placement="top"
                                title={
                                    <>
                                        <div>
                                            <Trans>
                                                Certification schemes like Forest Stewardship Council (FSC) or Roundtable on Sustainable Palm Oil (RSPO)
                                                require companies to demonstrate consultation with indigenous communities as part of their certification process
                                            </Trans>
                                        </div>
                                    </>
                                }
                                children={
                                    <InfoCircleTwoTone
                                        twoToneColor="#1890ff"
                                        style={{ marginLeft: 5 }}
                                    />
                                }
                            />
                            <div style={{marginBottom: 10}}>
                                <Upload {...certEgFSCProps}>
                                    <Button
                                        disabled={!indigenousPresence}
                                        style={{marginTop: 10}}
                                        icon={<FolderOpenOutlined/>}
                                    > <Trans>Select File</Trans></Button>
                                </Upload>
                            </div>
                            <div className="center-card">
                                <Button
                                    disabled={!indigenousPresence}
                                    loading={certEgFSCLoading} htmlType="button" type="primary"
                                    onClick={() => handleUploadFile('indigenous.certEgFSC', locationId)}>
                                    <ButtonWithIcon
                                        icon={<UploadOutlined/>}
                                        text={<Trans>Upload</Trans>}
                                    />
                                </Button>
                            </div>
                        </Form.Item>
                        <Divider></Divider>
                        <Form.Item style={{marginTop: 10}}>
                            <b><Trans>9. Review Reports from Third-Party Verifiers?</Trans></b>
                            <Tooltip
                                placement="top"
                                title={
                                    <>
                                        <div>
                                            <Trans>
                                                Some organizations, such as Rainforest Alliance or Fairtrade International,
                                                provide independent verification of consultation processes.
                                                They evaluate whether indigenous peoples have been properly consulted and whether their rights have been respected.
                                            </Trans>
                                        </div>
                                    </>
                                }
                                children={
                                    <InfoCircleTwoTone
                                        twoToneColor="#1890ff"
                                        style={{ marginLeft: 5 }}
                                    />
                                }
                            />
                            <div style={{marginBottom: 10}}>
                                <Upload {...reviewReportsFromThirdPartyVerifiers1Props}>
                                    <Button
                                        disabled={!indigenousPresence}
                                        style={{marginTop: 10}}
                                        icon={<FolderOpenOutlined/>}
                                    > <Trans>Select File</Trans></Button>
                                </Upload>
                            </div>
                            <div className="center-card">
                                <Button
                                    disabled={!indigenousPresence}
                                    loading={reviewReportsFromThirdPartyVerifiers1Loading} htmlType="button" type="primary"
                                    onClick={() => handleUploadFile('indigenous.reviewReportsFromThirdPartyVerifiers1', locationId)}>
                                    <ButtonWithIcon
                                        icon={<UploadOutlined/>}
                                        text={<Trans>Upload</Trans>}
                                    />
                                </Button>
                            </div>
                        </Form.Item>
                        <Divider></Divider>
                        <b><Trans>10. Has consultation and cooperation in good faith with the concerned indigenous communities
                            taken place?</Trans></b>
                        <Form.Item
                            name={[ "indigenous", "consultationWithIndigenousCommunities"]}
                            rules={[{required: indigenousPresence, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                disabled={!indigenousPresence}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                    <Radio value={3}>Unknown</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Divider></Divider>
                        <b><Trans>11. Has any indigenous group (or local community) submitted a legal,
                            documented claim to the land or there is court litigation, land dispute, or ongoing negotiation? If yes, please provide a high-level description.
                        </Trans></b>
                        <Tooltip
                            placement="top"
                            title={
                                <>
                                    <div>
                                        <Trans>
                                            UN Declaration on the Rights of Indigenous Peoples (UNDRIP) and International Labour Organization (ILO) Convention 169
                                        </Trans>
                                    </div>
                                </>
                            }
                            children={
                                <InfoCircleTwoTone
                                    twoToneColor="#1890ff"
                                    style={{ marginLeft: 5 }}
                                />
                            }
                        />
                        <Form.Item
                            name={[ "indigenous", "hasAnyLegalAndDocumentedClaimToTheLand"]}
                            rules={[{required: indigenousPresence, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                disabled={!indigenousPresence}
                                onChange={(e) => {
                                    setDocumentedClaimValue(e.target.value);
                                    form.validateFields([[ "indigenous", "hasAnyLegalAndDocumentedClaimToTheLandDescription"]]);
                                }}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            style={{ marginTop: 10 }}
                            name={[ "indigenous", "hasAnyLegalAndDocumentedClaimToTheLandDescription"]}
                            rules={[{ required: documentedClaimValue === 1, message: 'Please write the description' }]}
                        >
                            <TextArea rows={4} disabled={documentedClaimValue !== 1 || !indigenousPresence} placeholder="Please write the description..." />
                        </Form.Item>
                        <Divider></Divider>
                        <b>
                            <Trans>
                                12. Requested data (in English translation) from National or Local Land registry.
                                Please write a few sentences about what it contains.
                                Indicate the date when you can provide the data from National or Local Land Registry. (if any)
                            </Trans>
                        </b>
                        <Form.Item
                            style={{marginBottom: 0}}
                            name={[ "indigenous", "dataFromNationalOrLocalLandRegistry"]}
                        >
                            <div style={{marginBottom: 10}}>
                                <Upload {...dataFromNationalOrLocalLandRegistryProps}>
                                    <Button
                                        disabled={!indigenousPresence}
                                        style={{marginTop: 10}}
                                        icon={<FolderOpenOutlined/>}
                                    > <Trans>Select File</Trans></Button>
                                </Upload>
                            </div>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={[ "indigenous", "dataFromNationalOrLocalLandRegistryDeadline"]}
                        >
                            <DatePicker disabled={!indigenousPresence}/>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={[ "indigenous", "dataFromNationalOrLocalLandRegistryDescription"]}
                        >
                            <TextArea disabled={!indigenousPresence} rows={4} placeholder="What contains the provided document?"/>
                        </Form.Item>
                        <div className="center-card">
                            <Button loading={dataFromNationalOrLocalLandRegistryLoading} htmlType="button" type="primary"
                                    onClick={() => handleUploadFile("indigenous.dataFromNationalOrLocalLandRegistry", locationId)}>
                                <ButtonWithIcon
                                    icon={<UploadOutlined/>}
                                    text={<Trans>Upload</Trans>}
                                />
                            </Button>
                        </div>
                        <Divider></Divider>
                        <b><Trans>13. Are there any public authority statements or records of non-consensual land use?</Trans></b>
                        <Tooltip
                            placement="top"
                            title={
                                <>
                                    <div>
                                        <Trans>
                                            UN Declaration on the Rights of Indigenous Peoples (UNDRIP) and International Labour Organization (ILO) Convention 169
                                        </Trans>
                                    </div>
                                </>
                            }
                            children={
                                <InfoCircleTwoTone
                                    twoToneColor="#1890ff"
                                    style={{ marginLeft: 5 }}
                                />
                            }
                        />
                        <Form.Item
                            name={[ "indigenous", "anyPublicStatementsOrRecordsOfNonConsensualLandUse"]}
                            rules={[{required: true, message: 'Please select an option'}]}
                        >
                            <Radio.Group
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setAnyPublicStatement(value === 1);
                                }}
                            >
                                <Space direction="horizontal">
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={2}>No</Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Divider></Divider>
                        <b>
                            <Trans>14. Please provide the relevant public authority statements in English. Please
                                write a
                                few sentences about what it contains.
                            </Trans>
                        </b>
                        <Form.Item
                            style={{marginBottom: 0}}
                            name={[ "indigenous", "publicStatements"]}
                        >
                            <div style={{marginBottom: 10}}>
                                <Upload {...publicStatementsProps}>
                                    <Button
                                        disabled={!anyPublicStatement}
                                        style={{marginTop: 10}}
                                        icon={<FolderOpenOutlined/>}
                                    > <Trans>Select File</Trans></Button>
                                </Upload>
                            </div>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={[ "indigenous", "publicStatementsDescription"]}
                        >
                            <TextArea disabled={!anyPublicStatement} rows={4} placeholder="What contains the provided document?"/>
                        </Form.Item>
                        <div className="center-card">
                            <Button
                                disabled={!anyPublicStatement}
                                loading={publicStatementsLoading} htmlType="button" type="primary"
                                    onClick={() => handleUploadFile("indigenous.publicStatements", locationId)}>
                                <ButtonWithIcon
                                    icon={<UploadOutlined/>}
                                    text={<Trans>Upload</Trans>}
                                />
                            </Button>
                        </div>
                    </Card>
                </Form>);
            default:
                return <>What?</>;
        }
    }

    return (
        <>
            {renderContent(currentState)}
            <>
                <Row gutter={16}>
                    <Col span={24}>
                        <div
                            style={{
                                position: "fixed",
                                bottom: 0,
                                right: 0,
                                width: 1020,
                                background: "#fff",
                                borderTop: "1px solid #e8e8e8",
                                padding: "10px 20px",
                                zIndex: 100,
                                textAlign: "right"
                            }}
                        >
                            <Space>
                                <Button disabled={currentState == 0} htmlType="button"
                                        onClick={() => handlePrev()} style={{float: 'left', marginRight: 10}}>
                                    <ButtonWithIcon
                                        icon={<LeftOutlined/>}
                                        text={<Trans>Back</Trans>}
                                    />
                                </Button>

                                {
                                    currentState == 0
                                        ?
                                        <Button htmlType="button"
                                                type={"primary"}
                                                onClick={() => handleNext()} style={{float: 'left', marginRight: 10}}>
                                            <ButtonWithIconSuff
                                                icon={<RightOutlined/>}
                                                text={<Trans>Next</Trans>}
                                            />
                                        </Button>
                                        :
                                        <Button htmlType="button"
                                                type={"primary"}
                                                loading={loading}
                                                onClick={() => saveLocationData(locationId)} style={{float: 'left', marginRight: 10}}>
                                            <Trans>Save</Trans>
                                        </Button>
                                }
                            </Space>
                        </div>
                    </Col>
                </Row>

            </>
        </>
);
};

export default AddLocationForm;
